import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import { UpdateNotice } from "@/components/UpdateNotice";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Board from "@/pages/Board";
import CustomerList from "@/pages/CustomerList";
import CustomerDetail from "@/pages/CustomerDetail";
import AdWorkflow from "@/pages/AdWorkflow";
import AdWorkflowPartner from "@/pages/AdWorkflowPartner";
import DuUpload from "@/pages/DuUpload";
import LogoUpload from "@/pages/LogoUpload";
import PublicGallery from "@/pages/PublicGallery";
import BookingPage from "@/pages/BookingPage";
import OpenBookingPage from "@/pages/OpenBookingPage";
import OptOutPage from "@/pages/OptOutPage";
import AdFormatsSettings from "@/pages/settings/AdFormatsSettings";
import ServiceCatalogSettings from "@/pages/settings/ServiceCatalogSettings";
import Emails from "@/pages/Emails";
import SettingsLayout from "@/pages/settings/SettingsLayout";
import GeneralSettings from "@/pages/settings/GeneralSettings";
import LabelsSettings from "@/pages/settings/LabelsSettings";
import LiveMetaSettings from "@/pages/settings/LiveMetaSettings";
import SecuritySettings from "@/pages/settings/SecuritySettings";
import NotificationsSettings from "@/pages/settings/NotificationsSettings";
import TeamScheduleSettings from "@/pages/settings/TeamScheduleSettings";
import FooterSettings from "@/pages/settings/FooterSettings";
import AccountingSettings from "@/pages/settings/AccountingSettings";
import Accounting from "@/pages/Accounting";
import CloudSettings from "@/pages/settings/CloudSettings";
import BackupSettings from "@/pages/settings/BackupSettings";
import MailSettings from "@/pages/settings/MailSettings";
import PeopleSettings from "@/pages/settings/PeopleSettings";
import ColorProfilesSettings from "@/pages/settings/ColorProfilesSettings";
import PersonDetail from "@/pages/settings/PersonDetail";
import UsersPage from "@/pages/UsersPage";
import UserAccessPage from "@/pages/UserAccessPage";
import AccountPage from "@/pages/AccountPage";
import OccupancyPlan from "@/pages/OccupancyPlan";
import BannerPlanner from "@/pages/BannerPlanner";
import BackdropDesigner from "@/pages/BackdropDesigner";
import TemplateEditPage from "@/pages/TemplateEditPage";
import { ShieldOff } from "lucide-react";

function Area({ name, children }) {
  const { user } = useAuth();
  if (name === "admin" ? user?.role === "admin" : (user?.permissions?.[name] || "none") !== "none") return children;
  return (
    <div className="p-10 text-center text-slate-500" data-testid="no-access">
      <ShieldOff className="mx-auto mb-3 text-slate-400" size={36} />
      <h1 className="font-display text-2xl font-bold text-slate-800">Kein Zugriff</h1>
      <p className="mt-1 text-sm">Für diesen Bereich wurde Ihnen keine Berechtigung erteilt.</p>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/du/:token" element={<DuUpload />} />
            <Route path="/logo/:token" element={<LogoUpload />} />
            <Route path="/galerie/:token" element={<PublicGallery />} />
            <Route path="/anzeige/:token" element={<BookingPage />} />
            <Route path="/anzeige-buchen" element={<OpenBookingPage />} />
            <Route path="/abmelden/:token" element={<OptOutPage />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Area name="dashboard"><Dashboard /></Area>} />
              <Route path="/ausgabe/:id" element={<Area name="ausgaben"><Board /></Area>} />
              <Route path="/kunden" element={<Navigate to="/partner" replace />} />
              <Route path="/partner" element={<Area name="partner"><CustomerList key="partner" type="partner" /></Area>} />
              <Route path="/partner/anzeigen" element={<Area name="partner"><AdWorkflow key="wf-p" type="partner" /></Area>} />
              <Route path="/partner/anzeigen/:id" element={<Area name="partner"><AdWorkflowPartner key="wfd-p" type="partner" /></Area>} />
              <Route path="/partner/belegungsplan" element={<Area name="partner"><OccupancyPlan /></Area>} />
              <Route path="/partner/backdrops" element={<Area name="partner"><BackdropDesigner /></Area>} />
              <Route path="/partner/backdrops/:spaceId" element={<Area name="partner"><BackdropDesigner /></Area>} />
              <Route path="/partner/werbebanner" element={<Area name="partner"><BannerPlanner /></Area>} />
              <Route path="/partner/werbebanner/:spaceId" element={<Area name="partner"><BannerPlanner /></Area>} />
              <Route path="/partner/leistungskatalog" element={<Area name="admin"><div className="p-6 lg:p-8"><ServiceCatalogSettings /></div></Area>} />
              <Route path="/partner/:id" element={<Area name="partner"><CustomerDetail key="partner-d" type="partner" /></Area>} />
              <Route path="/bezahlkunden/*" element={<Navigate to="/anzeigenkunden" replace />} />
              <Route path="/anzeigenkunden" element={<Area name="bezahlkunden"><CustomerList key="bezahlkunde" type="bezahlkunde" /></Area>} />
              <Route path="/anzeigenkunden/anfragen" element={<Area name="bezahlkunden"><AdWorkflow key="wf-b" type="bezahlkunde" /></Area>} />
              <Route path="/anzeigenkunden/anfragen/:id" element={<Area name="bezahlkunden"><AdWorkflowPartner key="wfd-b" type="bezahlkunde" /></Area>} />
              <Route path="/anzeigenkunden/:id" element={<Area name="bezahlkunden"><CustomerDetail key="bezahlkunde-d" type="bezahlkunde" /></Area>} />
              <Route path="/mails" element={<Area name="mails"><Emails /></Area>} />
              <Route path="/buchhaltung" element={<Area name="buchhaltung"><Accounting /></Area>} />
              <Route path="/mails/vorlage/:id" element={<Area name="mails"><TemplateEditPage /></Area>} />
              <Route path="/benutzer" element={<Area name="admin"><UsersPage /></Area>} />
              <Route path="/konto" element={<Area name="admin"><AccountPage /></Area>} />
              <Route path="/benutzer/:personId" element={<Area name="admin"><UserAccessPage /></Area>} />
              <Route path="/einstellungen" element={<SettingsLayout />}>
                <Route index element={<GeneralSettings />} />
                <Route path="live" element={<LiveMetaSettings />} />
                <Route path="mail" element={<MailSettings />} />
                <Route path="mitwirkende" element={<Area name="mitwirkende"><PeopleSettings /></Area>} />
                <Route path="mitwirkende/:id" element={<Area name="mitwirkende"><PersonDetail /></Area>} />
                <Route path="anzeigenformate" element={<Area name="einstellungen"><AdFormatsSettings /></Area>} />
                <Route path="leistungskatalog" element={<Navigate to="/partner/leistungskatalog" replace />} />
                <Route path="sicherheit" element={<SecuritySettings />} />
                <Route path="benachrichtigungen" element={<NotificationsSettings />} />
                <Route path="team" element={<TeamScheduleSettings />} />
                <Route path="labels" element={<LabelsSettings />} />
                <Route path="farbprofile" element={<ColorProfilesSettings />} />
                <Route path="cloud" element={<CloudSettings />} />
                <Route path="footer" element={<FooterSettings />} />
                <Route path="buchhaltung" element={<AccountingSettings />} />
                <Route path="backup" element={<BackupSettings />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <UpdateNotice />
      </AuthProvider>
    </div>
  );
}

export default App;
